// src/pages/bloomterminal/alerts/steps/RegionSelector.jsx
import React, { useState, useEffect } from 'react';
import { ChevronUp, Search } from 'lucide-react';
import { Check } from 'lucide-react';
import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';

const RegionSelector = ({ regions, selectedRegions, onRegionToggle, onSelectAll, onClearAll, isLoading = false }) => {
  const { fontSize } = useGlobalFontSize();
  // All parent regions are expanded by default
  const [expandedParents, setExpandedParents] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Update expanded state when regions change
  useEffect(() => {
    const expanded = {};
    Object.keys(regions || {}).forEach(key => {
      expanded[key] = true;
    });
    setExpandedParents(expanded);
  }, [regions]);

  const toggleParent = (parentKey) => {
    setExpandedParents(prev => ({
      ...prev,
      [parentKey]: !prev[parentKey],
    }));
  };

  const RegionCheckbox = ({ checked, onChange, label }) => {
    const { fontSize: checkboxFontSize } = useGlobalFontSize();
    return (
      <div className="flex items-start gap-2 cursor-pointer" onClick={onChange}>
        <div
          className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 mt-1 ${
            checked
              ? 'border-bloom-green bg-bloom-green'
              : 'border-bloom-green bg-transparent'
          }`}
        >
          {checked && <Check size={12} className="text-white" strokeWidth={3} />}
        </div>
        <span 
          className="font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2] break-words"
          style={{ fontSize: checkboxFontSize }}
        >
          {label}
        </span>
      </div>
    );
  };

  const ParentRegionCard = ({ parentKey, parentLabel, children }) => {
    const isExpanded = expandedParents[parentKey];
    const parentSelectedRegions = selectedRegions[parentKey] || [];
    
    const validChildren = (Array.isArray(children) ? children : []).filter(child => child != null);
    const isEmpty = validChildren.length === 0;
    const isRecommended = String(parentKey || '').toLowerCase().includes('recommended');
    
    const isCountrySelected = isEmpty && !isRecommended && parentSelectedRegions.includes(parentLabel);

    if (isEmpty && !isRecommended) {
      return (
        <div className="border border-[#828382] rounded-[8px] px-3 py-2 flex items-center w-full">
          <RegionCheckbox
            checked={isCountrySelected}
            onChange={() => onRegionToggle(parentKey, parentLabel)}
            label={parentLabel}
          />
        </div>
      );
    }

    return (
      <div className="border border-[#828382] rounded-[8px] overflow-hidden flex flex-col w-full flex-shrink-0">
        {/* Header */}
        <div className="flex flex-col gap-3 px-3 pt-2 pb-2 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span 
              className={`font-['Inter'] font-semibold ${isRecommended ? 'text-bloom-green uppercase' : 'text-[#282A29] dark:text-white'}`}
              style={{ fontSize: fontSize }}
            >
              {parentLabel}
            </span>
            <button
              onClick={() => toggleParent(parentKey)}
              className="ml-auto flex-shrink-0"
            >
              <ChevronUp
                size={16}
                className={`text-[#282A29] dark:text-[#f0f5f2] transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
          {!isEmpty && (
            <div className="flex gap-[15px] items-center">
              <button
                onClick={() => onSelectAll(parentKey, validChildren)}
                className="font-['Helvetica_Neue'] font-medium leading-[21px] text-bloom-green hover:opacity-80 transition-opacity"
                style={{ fontSize: fontSize }}
              >
                Select all
              </button>
              <button
                onClick={() => onClearAll(parentKey)}
                className={`font-['Helvetica_Neue'] font-medium leading-[21px] ${
                  isRecommended ? 'text-[#828382]' : 'text-[#ec3460]'
                } hover:opacity-80 transition-opacity`}
                style={{ fontSize: fontSize }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Children Regions Grid - Responsive with minmax; max-height so long lists scroll */}
        {isExpanded && !isEmpty && (
          <div className="overflow-y-auto overflow-x-hidden px-3 pb-2 min-w-0 max-h-[320px]">
            <div 
              className="grid gap-x-4 gap-y-2"
              style={{
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))'
              }}
            >
              {validChildren.map((region, idx) => (
                <RegionCheckbox
                  key={`${parentKey}-${idx}`}
                  checked={parentSelectedRegions.includes(region)}
                  onChange={() => onRegionToggle(parentKey, region)}
                  label={region}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 flex-1">
        <h3 
          className="font-['Inter'] font-medium text-[#282A29] dark:text-white"
          style={{ fontSize: fontSize }}
        >
          Select regions to receive alerts for
        </h3>
        <div role="status" aria-busy="true" aria-label="Loading" className="flex h-[195px] flex-col gap-3 rounded-[8px] border border-[#828382] p-3">
          <div className="skeleton-sheen h-10 w-full rounded-[8px]" />
          <div className="skeleton-sheen h-10 w-full rounded-[8px]" />
          <div className="skeleton-sheen h-10 w-full rounded-[8px]" />
        </div>
      </div>
    );
  }

  // Sort: USA first, then Recommended, then the rest (alphabetically)
  const regionEntries = Object.entries(regions || {}).sort(([keyA], [keyB]) => {
    const a = String(keyA || '').toLowerCase();
    const b = String(keyB || '').toLowerCase();
    const aIsUSA = a === 'usa' || a === 'united states';
    const bIsUSA = b === 'usa' || b === 'united states';
    const aIsRecommended = a.includes('recommended');
    const bIsRecommended = b.includes('recommended');
    if (aIsUSA && !bIsUSA) return -1;
    if (!aIsUSA && bIsUSA) return 1;
    if (aIsRecommended && !bIsRecommended) return -1;
    if (!aIsRecommended && bIsRecommended) return 1;
    return a.localeCompare(b);
  });

  // Filter by search; always include Recommended
  const query = (searchQuery || '').trim().toLowerCase();
  const filteredEntries = query
    ? regionEntries.filter(([key]) => {
        const k = String(key || '').toLowerCase();
        if (k.includes('recommended')) return true;
        return k.includes(query);
      })
    : regionEntries;

  if (regionEntries.length === 0) {
    return (
      <div className="flex flex-col gap-6 flex-1">
        <h3 
          className="font-['Inter'] font-medium text-[#282A29] dark:text-white"
          style={{ fontSize: fontSize }}
        >
          Select regions to receive alerts for
        </h3>
        <div className="flex items-center justify-center h-[195px] border border-[#828382] rounded-[8px]">
          <p 
            className="font-['Inter'] text-[#828382]"
            style={{ fontSize: fontSize }}
          >
            No regions available for this selection
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 flex-1 min-w-0">
      <h3 className="font-['Inter'] font-medium text-[14px] leading-[22px] text-[#282A29] dark:text-white flex-shrink-0">
        Select regions to receive alerts for
      </h3>
      <div className="relative flex-shrink-0">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#828382] pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search the country"
          className="w-full pl-9 pr-3 py-2 rounded-[8px] border border-[#828382] bg-transparent font-['Inter'] text-[#282A29] dark:text-[#f0f5f2] placeholder:text-[#828382] focus:outline-none focus:ring-1 focus:ring-bloom-green focus:border-bloom-green"
          style={{ fontSize: fontSize }}
        />
      </div>
      <div className="flex flex-col gap-6 min-w-0 w-full max-h-[400px] overflow-y-auto overflow-x-hidden pr-1 flex-shrink min-h-0">
        {filteredEntries.map(([parentKey, children]) => (
          <ParentRegionCard
            key={parentKey}
            parentKey={parentKey}
            parentLabel={parentKey}
            children={children}
          />
        ))}
      </div>
    </div>
  );
};

export default RegionSelector;

